import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import _ from 'lodash';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { launchPreview, userRegistration } from '../../actions/ScormActions';
import { SCORM_CONTENT_TYPE_OPTIONS, SCORM_LAUNCH_OPTIONS } from '../../constants/ScormEnums';
import { CERTIFICATION_PAGE } from '../../constants/Urls';
import { useFormSelector } from '../../custom-hooks';
import { getUITA } from '../../utils/commonUtils';
import { getEnvVar } from '../../utils/env';
import { getDomainUrl } from '../../utils/qs';
import ControlChangesIcon from '../components/ControlChangesIcon';
import { FORM_KEYS } from '../constants/form-manager.constants';
import { FCM_BUTTON_STYLES } from '../constants/runtime.constants';
import {
  getIsFormChangesMode,
  getWorkflowInfo,
  isFormLoading,
} from '../selectors/common.selectors';
import { getCourseVersions } from '../selectors/runtime.selectors';

const useStyles = makeStyles(FCM_BUTTON_STYLES);

const SSButtonScorm = ({
  scormContentTypeOptions: contentTypeOption = 'INITIAL',
  scormLaunchOptions: launchOption = 'LAUNCH',
  isEditor,
  label = 'Scorm Button',
  id,
  isControlHiddenFCM,
  isControlAddedFCM,
  isControlChangedFCM,
}) => {
  const classes = useStyles({ isControlAddedFCM });
  const dispatch = useDispatch();
  const { search } = useLocation();
  const courseVersions = useFormSelector(getCourseVersions, { formKey: FORM_KEYS.DETAIL });
  const { itmId, stepId } = useFormSelector(getWorkflowInfo);
  const isFormChangesMode = useFormSelector(getIsFormChangesMode);
  const isDetailFormLoading = useSelector(isFormLoading(FORM_KEYS.DETAIL));
  const { actorCode, actorFirstName, actorLastName } = useSelector((state) => state.auth);
  const [url, setUrl] = useState(null);

  const appUrl = new URL(getEnvVar('REACT_APP_ADMIN_APP_URL'));
  const baseURL = appUrl.origin;
  const contentType = SCORM_CONTENT_TYPE_OPTIONS[contentTypeOption];
  const launch = SCORM_LAUNCH_OPTIONS[launchOption];
  const courseVersion = courseVersions[contentType];
  const isLaunch = launch === SCORM_LAUNCH_OPTIONS.LAUNCH;

  useEffect(() => {
    const setButtonScormUrl = (data) => {
      if (data.hasError) {
        return;
      }

      if (isLaunch) {
        data.launchLink && setUrl(data.launchLink);
      } else {
        data.PreviewLink && setUrl(data.PreviewLink);
      }
    };

    const runUserRegistration = async () => {
      const { reqId, rpgId } = qs.parse(search);
      const domainUrl = getDomainUrl();
      const pagePath = `${domainUrl}${CERTIFICATION_PAGE}`;
      const { href } = window.location;

      if (!href.includes(pagePath)) {
        return;
      }

      const courseId = reqId || itmId;
      const certificationType = rpgId || stepId;
      const redirectOnExitUrl = `${domainUrl}/close`;

      switch (launch) {
        case SCORM_LAUNCH_OPTIONS.LAUNCH: {
          if (
            _.isNil(courseId) ||
            _.isNil(certificationType) ||
            _.isNil(contentType) ||
            _.isNil(courseVersion)
          ) {
            return;
          }

          const requestData = {
            registration: {
              courseId: `CourseId|${courseId}!CertificationType|${contentType}`,
              learner: {
                id: actorCode,
                firstName: actorFirstName,
                lastName: actorLastName,
              },
              registrationId: `CourseId|${courseId}!CertificationType|${contentType}!UserName|${actorCode}!RpgId|${certificationType}`,
            },
            launchLink: {
              redirectOnExitUrl: window.location.href,
            },
            CourseVersion: courseVersion,
          };

          const responseData = await dispatch(userRegistration(requestData));

          responseData && setButtonScormUrl(responseData);
          return;
        }

        case SCORM_LAUNCH_OPTIONS.PREVIEW: {
          if (_.isNil(courseId) || _.isNil(contentType)) {
            return;
          }

          const requestData = {
            courseId: `CourseId|${courseId}!CertificationType|${contentType}`,
            redirectOnExitUrl,
          };

          const responseData = await dispatch(launchPreview(requestData));

          responseData && setButtonScormUrl(responseData);
          return;
        }

        default:
          return;
      }
    };

    if (!isDetailFormLoading) {
      runUserRegistration();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDetailFormLoading]);

  const onClickHandler = () => {
    window.open(`${baseURL}${url}`, isLaunch ? '_self' : '_blank');
  };

  return !url && !isEditor && !isFormChangesMode ? null : (
    <span
      className={isControlAddedFCM || isControlChangedFCM ? classes.changesModeWrapper : null}
      ui-ta={getUITA('FormCtrl', 'SSButtonScorm')}
      data-ss-id={id}
    >
      <Button
        color="primary"
        variant={isLaunch ? 'primary' : 'secondary'}
        style={{ minWidth: '128px' }}
        onClick={onClickHandler}
        disabled={isEditor || !url || isControlHiddenFCM}
      >
        {label}
      </Button>
      {isControlChangedFCM && (
        <span className={classes.changesIcon}>
          <ControlChangesIcon id={id} />
        </span>
      )}
    </span>
  );
};

export default SSButtonScorm;
